import styles from './FloatingTextModule.scss';

import cx from 'classnames';
import { ReferencedEntry } from 'contentful';
import React from 'react';

import { FloatingTextAssembly, HasVideo, TextAlignment, Topic } from 'src/api/contentful/contentTypes.cloverdotcom';

import { isEntry } from 'src/utils/EntryUtil';
import { FadeToastUp } from '../Animation/FadeToastUp/FadeToastUp';
import { EnhancedMedia } from '../EnhancedMedia/EnhancedMedia';
import { withBrowser } from '../Responsive/WithBrowser';
import { TextBox } from './TextBox/TextBox';

interface IFloatingTextModulePresentational extends React.AllHTMLAttributes<HTMLDivElement> {
  topic?: ReferencedEntry<Topic>;
  mediaOverride?: ReferencedEntry<HasVideo>;
  textBoxAlignment?: TextAlignment.LEFT | TextAlignment.RIGHT;
  ctaButton?: boolean;
  isFullWidthVariation?: boolean;
  isGreaterThanMobile?: boolean;
  isGreaterThanTablet?: boolean;
  isMobile?: boolean;
  isLessThanDesktop?: boolean;
  titleTagName?: keyof React.ReactHTML;
}

const FloatingTextModulePresentational: React.FC<IFloatingTextModulePresentational> = ({
  topic,
  mediaOverride,
  textBoxAlignment = TextAlignment.LEFT,
  ctaButton,
  className,
  isFullWidthVariation = false,
  isGreaterThanMobile,
  isGreaterThanTablet,
  isMobile,
  isLessThanDesktop,
  titleTagName,
  ...props
}) => (
  <FadeToastUp>
    <div className={cx(styles.moduleWrapper, className)} {...props}>
      <div
        className={cx(styles.container, {
          [styles.containerLeft]: !isFullWidthVariation && textBoxAlignment === TextAlignment.LEFT,
          [styles.containerRight]: !isFullWidthVariation && textBoxAlignment === TextAlignment.RIGHT,
          [styles.containerFullWidth]: isFullWidthVariation,
        })}
      >
        <EnhancedMedia
          preferWidth={isFullWidthVariation ? isGreaterThanMobile : isGreaterThanTablet}
          preferHeight={isFullWidthVariation ? isMobile : isLessThanDesktop}
          className={styles.mediaWrapper}
          entry={mediaOverride || topic}
        />
        <FadeToastUp>
          <div className={styles.textWrapper} >
            <TextBox
              entry={topic}
              ctaButton={ctaButton}
              titleTagName={titleTagName}
            />
          </div>
        </FadeToastUp>
      </div>
    </div>
  </FadeToastUp>
);

const Browsered = withBrowser(browser => ({
  isGreaterThanMobile: browser.greaterThan.mobile,
  isGreaterThanTablet: browser.greaterThan.tablet,
  isMobile: browser.is.mobile,
  isLessThanDesktop: browser.lessThan.desktop,
}))(FloatingTextModulePresentational);

interface IFloatingTextModule extends Pick<IFloatingTextModulePresentational, Exclude<keyof IFloatingTextModulePresentational, 'topic'>> {
  entry?: ReferencedEntry<FloatingTextAssembly>;
}

const FloatingTextModule: React.FC<IFloatingTextModule> = ({
  entry, ...props
}) => {
  if (!isEntry(entry)) return null;

  const { textBoxAlignment, isCtaButton, topic } = entry.fields;

  return (
    <Browsered
      topic={topic}
      textBoxAlignment={textBoxAlignment}
      ctaButton={isCtaButton}
      {...props}
    />
  );
};

export {
  IFloatingTextModulePresentational,
  Browsered as FloatingTextModulePresentational,
  FloatingTextModule,
};
